/* WCAG 2.2 AA — runtime enhancement for APP-INJECTED DOM only.
   Bold/bundle widget, the easyslider description accordion and the chat launcher
   render their markup via third-party scripts we cannot edit in Liquid, so we
   repair them after they appear and on later re-renders. Theme-code fixes live
   in a11y.css and the templates; this file is strictly for what the apps inject.
   See docs/a11y-audit/. Safe to run repeatedly (idempotent + guarded). */
(function () {
  'use strict';

  function clean(t) {
    return (t || '').replace(/this item:?/ig, '').replace(/£[\d.,]+/g, '').replace(/\s+/g, ' ').trim();
  }

  // 1. Bundle/upsell add-on checkboxes have no accessible name. The labelled
  //    one-time/subscribe radios (wrapped in <label>) are deliberately skipped.
  function fixBundleCheckboxes() {
    document.querySelectorAll('input.svelte-trfe7m, .bundle-container input[type="checkbox"], .dai-asset-container input[type="checkbox"]').forEach(function (cb) {
      if (cb.type === 'radio') return;           // leave the labelled one-time/subscribe radios alone (avoids 2.5.3)
      if (cb.getAttribute('aria-label')) return; // already named on an earlier pass
      // These checkboxes are visually-hidden, so they carry no visible label of
      // their own; the product name lives in the wrapping .bundle-item-checkbox
      // cell (e.g. "This item: 30 Day Supply £129.99"). The app sometimes wraps
      // them in a <label> that screen readers do not treat as a name, so we set an
      // explicit aria-label rather than trusting that wrapper. clean() drops the
      // "this item" prefix and the prices, leaving the product name.
      var item = cb.closest('.bundle-item-checkbox, .bundle-col-auto, .bundle-item, [data-handle], li') || cb.parentElement;
      var label = clean(item && item.textContent);
      if (!label) {
        var h = cb.closest('[data-handle]');
        if (h && h.dataset.handle) label = h.dataset.handle.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      }
      if (label) cb.setAttribute('aria-label', label.slice(0, 80));
    });
  }

  // 2. Description accordion is exposed as a tablist (with aria-selected on
  //    headers and aria-expanded on panels) but is not tabs. Convert it to a
  //    valid disclosure: header = button with aria-expanded, panel hidden when
  //    collapsed. Re-synced on toggle via the observer.
  function fixEasyslider() {
    // The app exposes the description accordion as role="tablist" and re-applies
    // that role on its own re-renders. The main observer below only watches
    // childList, so it misses this attribute change. Strip the role now and attach
    // a one-time attribute observer per element so any re-add is removed at once.
    document.querySelectorAll('.easyslider-contents').forEach(function (c) {
      if (c.getAttribute('role') === 'tablist') c.removeAttribute('role');
      if (!c.dataset.a11yRoleGuard) {
        c.dataset.a11yRoleGuard = '1';
        new MutationObserver(function () {
          if (c.getAttribute('role') === 'tablist') c.removeAttribute('role');
        }).observe(c, { attributes: true, attributeFilter: ['role'] });
      }
    });
    document.querySelectorAll('.easyslider-item').forEach(function (item) {
      var header = item.querySelector('.easyslider-header');
      var content = item.querySelector('.easyslider-content');
      var open = item.classList.contains('easyslider-active');
      if (header) {
        header.setAttribute('role', 'button');
        header.removeAttribute('aria-selected');
        header.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (content && content.id) header.setAttribute('aria-controls', content.id);
      }
      if (content) {
        content.removeAttribute('aria-expanded');
        if (open) content.removeAttribute('aria-hidden');
        else content.setAttribute('aria-hidden', 'true');
      }
    });
  }

  // 3. Chat launcher buttons: give nameless icon buttons an accessible name and
  //    a 24px minimum target.
  function fixChatLauncher() {
    document.querySelectorAll('#launcher button, [id*="launcher"] button').forEach(function (b) {
      if (!b.getAttribute('aria-label') && !clean(b.textContent)) {
        var c = b.className || '';
        var name = /send/i.test(c) ? 'Send message'
          : /minim/i.test(c) ? 'Minimise chat'
          : /close/i.test(c) ? 'Close chat' : 'Open chat';
        b.setAttribute('aria-label', name);
      }
      var r = b.getBoundingClientRect();
      if (r.height && r.height < 24 && b.style.minHeight !== '24px') b.style.minHeight = '24px';
      if (r.width && r.width < 24 && b.style.minWidth !== '24px') b.style.minWidth = '24px';
    });
  }

  // 4. Bold colours the price labels with a too-light grey inline; force AA.
  function fixPriceContrast() {
    document.querySelectorAll('.total-price-text, .this-item').forEach(function (el) {
      if (el.style.color !== 'rgb(89, 89, 89)') el.style.setProperty('color', '#595959', 'important');
      // The app fades the colour via a transition/animation, which lets a snapshot
      // catch a too-light mid-fade frame. Freeze it so the AA colour is steady.
      el.style.setProperty('animation', 'none', 'important');
      el.style.setProperty('transition', 'none', 'important');
    });
  }

  // 5. Slick carousel dots render as <li role="tab"><button></button></li>,
  //    nesting two interactive elements. The button is the control, so strip the
  //    tab role/state from the <li>.
  function fixSlickDots() {
    // The slick dots are a <ul class="slick-dots" role="tablist"> with
    // <li role="tab"> children. Stripping the tab role from the <li> alone leaves
    // the <ul>'s tablist role without its required tab children (aria-required-
    // children) and keeps the tablist role overriding the <ul>'s list semantics
    // (listitem). So clear the container role too, restoring a plain list.
    document.querySelectorAll('.slick-dots[role="tablist"]').forEach(function (ul) {
      ul.removeAttribute('role');
    });
    document.querySelectorAll('.slick-dots li[role="tab"], li[id^="slickDot"][role="tab"]').forEach(function (li) {
      li.removeAttribute('role');
      li.removeAttribute('aria-selected');
      li.removeAttribute('aria-controls');
    });
  }

  // 6. Form errors (login, account, contact) are server-rendered on a page
  //    reload with no live region and no focus moved to them, so a screen-reader
  //    user is dropped at the top of the page in silence (WCAG 3.3.1). Mark the
  //    error as an alert and move focus to it once, so it is announced.
  var formErrFocused = false;
  function fixFormErrors() {
    var err = document.querySelector('.form-message--error');
    if (!err || err.offsetParent === null) return;
    if (!err.getAttribute('role')) err.setAttribute('role', 'alert');
    if (formErrFocused) return;
    formErrFocused = true;

    // VoiceOver only reads the focused heading, not the error list beneath it, so
    // focusing the container is not enough. Announce the FULL message through a
    // dedicated visually-hidden assertive live region: create it empty first, then
    // populate it after a tick so the live region fires and the screen reader reads
    // the whole message. Move focus to the error too, for sighted keyboard users.
    var msg = err.textContent.replace(/\s+/g, ' ').trim();
    var live = document.getElementById('a11y-announce');
    if (!live) {
      live = document.createElement('div');
      live.id = 'a11y-announce';
      live.setAttribute('aria-live', 'assertive');
      live.setAttribute('aria-atomic', 'true');
      live.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;';
      document.body.appendChild(live);
    }
    if (!err.hasAttribute('tabindex')) err.setAttribute('tabindex', '-1');
    setTimeout(function () { try { err.focus(); } catch (e) {} }, 200);
    setTimeout(function () { live.textContent = msg; }, 450);
  }

  // 7. Globo mega-menu top-level dropdowns ("Products", "Shop") are exposed as
  //    plain <a href="#"> with no disclosure semantics, and the panel only opens
  //    on MOUSE hover/click — a keyboard Enter does nothing, so the menu's links
  //    are unreachable without a mouse (WCAG 2.1.1 Keyboard, Level A; 4.1.2). The
  //    panel's display is driven purely by the `gm-visible gm-active` classes on
  //    the parent <li>, so we reuse that exact mechanism: add the disclosure ARIA,
  //    and make Enter/Space toggle those classes. A "Products"-style toggle that
  //    only points at "#" becomes a button (it has no destination); "Shop", which
  //    is a real link to a page, keeps Enter navigating and uses Space to preview
  //    its submenu in place. aria-expanded is kept honest on mouse hover via a
  //    per-item class observer, Escape closes, and focus leaving the item collapses.
  var gmPanelSeq = 0;
  function gmIsOpen(li) { return /gm-(visible|active)/.test(li.className); }
  function gmSet(li, toggle, open) {
    if (open) { li.classList.add('gm-visible', 'gm-active'); }
    else { li.classList.remove('gm-visible', 'gm-active'); }
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  function fixGloboMenu() {
    document.querySelectorAll('li.gm-has-submenu').forEach(function (li) {
      var toggle = li.querySelector(':scope > a.gm-target') || li.querySelector(':scope > a');
      var panel = li.querySelector(':scope > .gm-submenu');
      if (!toggle || !panel) return;
      var href = toggle.getAttribute('href') || '';
      var isButtonish = href === '' || href === '#' || /#$/.test(href);

      if (!panel.id) panel.id = 'gm-panel-' + (++gmPanelSeq);
      toggle.setAttribute('aria-haspopup', 'true');
      toggle.setAttribute('aria-controls', panel.id);
      if (isButtonish) toggle.setAttribute('role', 'button');
      toggle.setAttribute('aria-expanded', gmIsOpen(li) ? 'true' : 'false');

      if (li.dataset.a11yGlobo) return; // handlers + observer already attached
      li.dataset.a11yGlobo = '1';

      // Keep state honest when Globo opens/closes the panel on hover or mouse click.
      new MutationObserver(function () {
        var open = gmIsOpen(li);
        if ((toggle.getAttribute('aria-expanded') === 'true') !== open) {
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
      }).observe(li, { attributes: true, attributeFilter: ['class'] });

      toggle.addEventListener('keydown', function (e) {
        var space = e.key === ' ' || e.key === 'Spacebar';
        if ((isButtonish && (e.key === 'Enter' || space)) || (!isButtonish && space)) {
          e.preventDefault();           // Space never scrolls; "#" never jumps to top
          gmSet(li, toggle, !gmIsOpen(li));
        } else if (e.key === 'Escape' && gmIsOpen(li)) {
          e.preventDefault();
          gmSet(li, toggle, false);
        }
      });
      // Escape from a focused link inside the panel closes and returns to the toggle.
      panel.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { gmSet(li, toggle, false); try { toggle.focus(); } catch (e2) {} }
      });
      // Tabbing out of the whole item collapses it. A null relatedTarget is a tap on
      // something unfocusable (iOS Safari never focuses links), not a keyboard move.
      li.addEventListener('focusout', function (e) {
        if (e.relatedTarget && !li.contains(e.relatedTarget) && gmIsOpen(li)) gmSet(li, toggle, false);
      });
    });
  }

  // iOS Safari doesn't focus a tapped link, so focus falls back to the nearest focusable
  // ancestor (Debut's trapFocus makes the header section tabindex=-1). Globo's own focusout
  // handler then closes the open submenu before the click lands, and the tap never
  // navigates. Focus moving to an ancestor only happens on a pointer tap, never on Tab, so
  // swallow that focusout before it reaches the menu.
  document.addEventListener('focusout', function (e) {
    var to = e.relatedTarget;
    if (to && to.contains(e.target) && e.target.closest && e.target.closest('.gm-menu')) {
      e.stopImmediatePropagation();
    }
  }, true);

  // 8. The mobile-nav drawer (.mobile-nav-wrapper) holds a full duplicate of the navigation
  //    (#mobile-menu-items) plus duplicate account icons, parked off-screen at translateY(-100%)
  //    when closed. A translateY offset does NOT remove the drawer from the keyboard tab order, so
  //    a keyboard user tabs through a hidden second copy of the whole menu after the account icons
  //    — on desktop (a duplicate of the visible nav) and on mobile (the closed drawer, before the
  //    hamburger is ever opened). `inert` removes the element and all its descendants from the tab
  //    order and the accessibility tree regardless of CSS or transform. The drawer is "open" only
  //    while it carries the theme's js-menu--is-open class, so we make it inert whenever it is
  //    closed — at any width — and lift inert the moment it opens. The hamburger toggle sits
  //    outside the wrapper, so it stays reachable to open the menu.
  function setMobileNavInert(w) {
    var open = w.classList.contains('js-menu--is-open');
    if (open && w.inert) w.inert = false;
    else if (!open && !w.inert) w.inert = true;
  }
  function fixDuplicateMobileNav() {
    document.querySelectorAll('.mobile-nav-wrapper').forEach(function (w) {
      setMobileNavInert(w);
      if (!w.__a11yClassObserved) {
        w.__a11yClassObserved = true;
        new MutationObserver(function () { setMobileNavInert(w); })
          .observe(w, { attributes: true, attributeFilter: ['class'] });
      }
    });
  }

  var observer;
  function run() {
    if (observer) observer.disconnect(); // avoid reacting to our own changes
    try { fixDuplicateMobileNav(); } catch (e) {}
    try { fixBundleCheckboxes(); } catch (e) {}
    try { fixEasyslider(); } catch (e) {}
    try { fixChatLauncher(); } catch (e) {}
    try { fixPriceContrast(); } catch (e) {}
    try { fixSlickDots(); } catch (e) {}
    try { fixFormErrors(); } catch (e) {}
    try { fixGloboMenu(); } catch (e) {}
    if (observer) observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') run();
  else document.addEventListener('DOMContentLoaded', run);

  var debounce;
  observer = new MutationObserver(function () { clearTimeout(debounce); debounce = setTimeout(run, 200); });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Late-loading widgets (chat especially) — a few extra passes.
  [1000, 3000, 6000].forEach(function (ms) { setTimeout(run, ms); });
})();
